'use client';
import { ReactNode, Suspense, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { SWRConfig } from 'swr/_internal';

import { NavigationEvents } from '@/components/internal/NavigationEvents';
import { Preloader } from '@/components/Preloader/Preloader';
import { REQUEST_RETRIES } from '@/helpers/constants';
import { ResponsiveProvider } from '@/modules/client';
import { useGAConfigHook } from '@/modules/googleAnalytics';
import { RealTimeProvider } from '@/modules/realTime/providers';
import theme from '@/theme';

const retryTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

import { GlobalStyles } from '@/styles/GlobalStyles';

export const ClientProviders = ({ children }: { children: ReactNode }) => {
  useGAConfigHook();

  useEffect(() => {
    return () => {
      retryTimeouts.forEach((timer) => clearTimeout(timer));
      retryTimeouts.clear();
    };
  }, []);

  return (
    <SWRConfig
      value={{
        onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
          const status = error?.status;
          if (status === 404) return;

          if (retryCount > REQUEST_RETRIES) return;

          const timer = setTimeout(() => {
            revalidate({ retryCount });
            retryTimeouts.delete(_key);
          }, 5000);

          retryTimeouts.set(_key, timer);
        },
      }}
    >
      <ThemeProvider theme={theme}>
        <ResponsiveProvider>
          <RealTimeProvider>
            <GlobalStyles theme={theme} />
            <Suspense fallback={<Preloader />}>
              <NavigationEvents />
              {children}
            </Suspense>
          </RealTimeProvider>
        </ResponsiveProvider>
      </ThemeProvider>
    </SWRConfig>
  );
};
